/**
 * Module-level pub/sub decoupling the Apollo `customFetch`/`errorLink`
 * (plain functions created in `clients/ApolloClientProvider.tsx`, outside
 * React) from `DeployCompatibility.tsx` (owns the /config.json re-check and
 * escalation-dialog state) — MEIS-13220, design.md D3/D6.
 *
 * `ApolloClientProvider.tsx` is an ANCESTOR of the mounted
 * `DeployCompatibility` component (see `app/index.tsx`), so a React context
 * defined by one and consumed by the other would need to flow the wrong
 * direction; a plain event bus avoids that cycle and keeps both signals
 * (never a direct state flip — only a request to re-check or to escalate).
 */
type Listener = () => void;

function createSignal() {
  let listeners: readonly Listener[] = [];

  return {
    emit(): void {
      listeners.forEach(listener => listener());
    },
    subscribe(listener: Listener): () => void {
      listeners = [...listeners, listener];
      return () => {
        listeners = listeners.filter(l => l !== listener);
      };
    },
  };
}

// Requested by `customFetch` when a response's `X-Api-Compat-Version`
// header differs from the bundled constant — never flips state directly,
// only asks for an immediate /config.json re-check (design.md D3).
export const deployCompatRecheckRequested = createSignal();

// Requested by the Apollo `errorLink` when a validation-class GraphQL error
// arrives and the reload guard refuses (an automatic reload already
// happened recently) — opens the prominent escalation dialog.
export const reloadEscalationRequested = createSignal();
